import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import * as companyApi from '../../services/company-api.js';
import * as agentApi from '../../services/agent-api.js';
import type { CompanyAgent, AgentTemplate, HireRequest, McpToolGroup, PersonSummary, SkillSummary } from '../../services/company-api.js';
import type { Agent, UpdateAgentRequest, ProviderInfo } from '../../types/agent.js';
import '../settings/cp-soul-editor.js';

type View = 'team' | 'hire' | 'pending';

@customElement('cp-company-team-overview')
export class CpCompanyTeamOverview extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) companyId!: number;
  @property({ type: Number }) ceoAgentId!: number;
  @property({ type: String }) companyType = '';

  @state() private _agents: CompanyAgent[] = [];
  @state() private _templates: AgentTemplate[] = [];
  @state() private _pendingRequests: HireRequest[] = [];
  @state() private _view: View = 'team';
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _selectedTemplate: AgentTemplate | null = null;
  @state() private _specialization = '';
  @state() private _reason = '';
  @state() private _submitting = false;
  @state() private _templateSearch = '';

  // Agent edit states
  @state() private _editAgent: Agent | null = null;
  @state() private _editName = '';
  @state() private _editProvider = 'anthropic';
  @state() private _editModel = 'claude-sonnet-4-6';
  @state() private _editProviders: ProviderInfo[] = [];
  @state() private _editModelsForProvider: string[] = [];
  @state() private _editMaxTokens = 4096;
  @state() private _editColor = '#6366f1';
  @state() private _editSaving = false;
  @state() private _editSuccess = '';

  // Tool configuration states
  @state() private _availableTools: McpToolGroup[] = [];
  @state() private _selectedTools: Set<string> = new Set();
  @state() private _expandedGroups: Set<string> = new Set();
  @state() private _toolsSaving = false;
  @state() private _toolsSuccess = '';

  // Person access states
  @state() private _availablePersons: PersonSummary[] = [];
  @state() private _selectedPersons: Set<number> = new Set();
  @state() private _personsSaving = false;
  @state() private _personsSuccess = '';

  // Skill configuration states
  @state() private _availableSkills: SkillSummary[] = [];
  @state() private _selectedSkills: Set<string> = new Set();
  @state() private _skillsSaving = false;
  @state() private _skillsSuccess = '';

  override connectedCallback(): void {
    super.connectedCallback();
    void this._loadTeam();
    void this._loadPendingRequests();
  }

  private async _loadTeam(): Promise<void> {
    if (!this.companyId) return;
    this._loading = true;
    try {
      this._agents = await companyApi.listCompanyAgents(this.companyId);
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden des Teams';
    } finally {
      this._loading = false;
    }
  }

  private async _loadTemplates(): Promise<void> {
    try {
      this._templates = await companyApi.listAgentTemplates(
        this.companyType ? { companyType: this.companyType } : undefined,
      );
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden der Templates';
    }
  }

  private async _loadPendingRequests(): Promise<void> {
    try {
      this._pendingRequests = await companyApi.listHireRequests(this.companyId);
    } catch {
      // non-critical
    }
  }

  private async _switchView(view: View): Promise<void> {
    this._view = view;
    this._error = '';
    if (view === 'hire' && this._templates.length === 0) {
      await this._loadTemplates();
    }
    if (view === 'pending') {
      await this._loadPendingRequests();
    }
  }

  private _selectTemplate(template: AgentTemplate): void {
    this._selectedTemplate = template;
    this._specialization = '';
  }

  private async _submitHireRequest(): Promise<void> {
    if (!this._selectedTemplate || !this._reason.trim() || !this.ceoAgentId) return;
    this._submitting = true;
    this._error = '';
    try {
      await companyApi.requestHire({
        company_id: this.companyId,
        ceo_agent_id: this.ceoAgentId,
        template_id: this._selectedTemplate.id,
        specialization: this._specialization.trim() || undefined,
        reason: this._reason.trim(),
      });
      this._selectedTemplate = null;
      this._specialization = '';
      this._reason = '';
      await this._loadPendingRequests();
      this._view = 'pending';
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Einreichen';
    } finally {
      this._submitting = false;
    }
  }

  private async _approveRequest(id: number): Promise<void> {
    try {
      await companyApi.approveHireRequest(id);
      await Promise.all([this._loadTeam(), this._loadPendingRequests()]);
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Genehmigen';
    }
  }

  private async _rejectRequest(id: number): Promise<void> {
    const comment = prompt('Grund fuer die Ablehnung (optional):') ?? undefined;
    try {
      await companyApi.rejectHireRequest(id, comment);
      await this._loadPendingRequests();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Ablehnen';
    }
  }

  private async _deactivateAgent(id: number): Promise<void> {
    if (!confirm('Agenten wirklich deaktivieren? Das Langzeitgedaechtnis bleibt erhalten.')) return;
    try {
      await companyApi.deactivateAgent(id);
      await this._loadTeam();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Deaktivieren';
    }
  }

  private async _deleteAgent(id: number): Promise<void> {
    if (!confirm('Agenten unwiderruflich loeschen? Alle Daten (Threads, Gedaechtnis, Strategien) werden entfernt.')) return;
    try {
      await agentApi.deleteAgent(id);
      if (this._editAgent?.id === id) {
        this._editAgent = null;
      }
      await this._loadTeam();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Loeschen';
    }
  }

  private async _openAgentEdit(agentId: number): Promise<void> {
    this._error = '';
    this._editSuccess = '';
    this._toolsSuccess = '';
    this._personsSuccess = '';
    this._skillsSuccess = '';
    try {
      const [agent, tools, persons, skills] = await Promise.all([
        agentApi.getAgent(agentId),
        this._availableTools.length === 0 ? companyApi.getAvailableTools() : Promise.resolve(this._availableTools),
        this._availablePersons.length === 0 ? companyApi.getAvailablePersons() : Promise.resolve(this._availablePersons),
        this._availableSkills.length === 0 ? companyApi.getAvailableSkills() : Promise.resolve(this._availableSkills),
      ]);
      this._editAgent = agent;
      this._editName = agent.name;
      this._editProvider = agent.provider ?? 'anthropic';
      this._editModel = agent.model;
      this._editMaxTokens = agent.max_tokens;
      this._editColor = agent.color;
      this._availableTools = tools;
      this._availablePersons = persons;
      this._availableSkills = skills;

      await this._loadEditProviders();

      // Parse current allowed_tools — empty array means "unrestricted" (all tools)
      const currentTools: string[] = JSON.parse(agent.allowed_tools || '[]');
      if (currentTools.length === 0) {
        this._selectedTools = new Set(tools.flatMap(g => g.tools.map(t => t.fullName)));
      } else {
        this._selectedTools = new Set(currentTools);
      }
      this._expandedGroups = new Set();

      // Parse current allowed_persons
      const currentPersons: number[] = JSON.parse(agent.allowed_persons || '[]');
      this._selectedPersons = new Set(currentPersons);

      // Parse current allowed_skills
      const currentSkills: string[] = JSON.parse(agent.allowed_skills || '[]');
      this._selectedSkills = new Set(currentSkills);
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden des Agents';
    }
  }

  private _closeAgentEdit(): void {
    this._editAgent = null;
    this._editSuccess = '';
    this._toolsSuccess = '';
    this._personsSuccess = '';
    this._skillsSuccess = '';
  }

  private async _loadEditProviders(): Promise<void> {
    try {
      const res = await fetch('/api/providers', { credentials: 'include' });
      if (res.ok) {
        this._editProviders = await res.json() as ProviderInfo[];
      }
      await this._loadEditModelsForProvider(this._editProvider);
    } catch {
      this._editProviders = [];
    }
  }

  private async _loadEditModelsForProvider(providerId: string): Promise<void> {
    try {
      const res = await fetch(`/api/providers/${encodeURIComponent(providerId)}/models`, { credentials: 'include' });
      if (res.ok) {
        this._editModelsForProvider = await res.json() as string[];
      }
    } catch {
      this._editModelsForProvider = [];
    }
  }

  private async _onEditProviderChange(e: Event): Promise<void> {
    const newProvider = (e.target as HTMLSelectElement).value;
    this._editProvider = newProvider;
    this._editSuccess = '';

    await this._loadEditModelsForProvider(newProvider);

    if (this._editModelsForProvider.length > 0 && !this._editModelsForProvider.includes(this._editModel)) {
      this._editModel = this._editModelsForProvider[0];
    }
  }

  private _onEditModelChange(e: Event): void {
    this._editModel = (e.target as HTMLSelectElement).value;
    this._editSuccess = '';
  }

  private _toggleToolGroup(server: string): void {
    const next = new Set(this._expandedGroups);
    if (next.has(server)) {
      next.delete(server);
    } else {
      next.add(server);
    }
    this._expandedGroups = next;
  }

  private _toggleTool(fullName: string): void {
    const next = new Set(this._selectedTools);
    if (next.has(fullName)) {
      next.delete(fullName);
    } else {
      next.add(fullName);
    }
    this._selectedTools = next;
    this._toolsSuccess = '';
  }

  private _toggleAllInGroup(group: McpToolGroup): void {
    const next = new Set(this._selectedTools);
    const allSelected = group.tools.every(t => next.has(t.fullName));
    for (const tool of group.tools) {
      if (allSelected) {
        next.delete(tool.fullName);
      } else {
        next.add(tool.fullName);
      }
    }
    this._selectedTools = next;
    this._toolsSuccess = '';
  }

  private async _saveAllowedTools(): Promise<void> {
    if (!this._editAgent || this._toolsSaving) return;
    this._toolsSaving = true;
    this._error = '';
    this._toolsSuccess = '';
    try {
      await companyApi.setAgentAllowedTools(this._editAgent.id, Array.from(this._selectedTools));
      this._toolsSuccess = 'Tools gespeichert';
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Speichern der Tools';
    } finally {
      this._toolsSaving = false;
    }
  }

  private _togglePerson(id: number): void {
    const next = new Set(this._selectedPersons);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    this._selectedPersons = next;
    this._personsSuccess = '';
  }

  private async _saveAllowedPersons(): Promise<void> {
    if (!this._editAgent || this._personsSaving) return;
    this._personsSaving = true;
    this._error = '';
    this._personsSuccess = '';
    try {
      await companyApi.setAgentAllowedPersons(this._editAgent.id, Array.from(this._selectedPersons));
      this._personsSuccess = 'Personen gespeichert';
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Speichern der Personen';
    } finally {
      this._personsSaving = false;
    }
  }

  private _toggleSkill(skillId: string): void {
    const next = new Set(this._selectedSkills);
    if (next.has(skillId)) {
      next.delete(skillId);
    } else {
      next.add(skillId);
    }
    this._selectedSkills = next;
    this._skillsSuccess = '';
  }

  private async _saveAllowedSkills(): Promise<void> {
    if (!this._editAgent || this._skillsSaving) return;
    this._skillsSaving = true;
    this._error = '';
    this._skillsSuccess = '';
    try {
      await companyApi.setAgentAllowedSkills(this._editAgent.id, Array.from(this._selectedSkills));
      this._skillsSuccess = 'Skills gespeichert';
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Speichern der Skills';
    } finally {
      this._skillsSaving = false;
    }
  }

  private async _saveAgentSettings(): Promise<void> {
    if (!this._editAgent || this._editSaving) return;
    if (!this._editName.trim()) {
      this._error = 'Name ist erforderlich';
      return;
    }
    this._editSaving = true;
    this._error = '';
    this._editSuccess = '';
    try {
      const data: UpdateAgentRequest = {
        name: this._editName.trim(),
        provider: this._editProvider,
        model: this._editModel,
        max_tokens: this._editMaxTokens,
        color: this._editColor,
      };
      await agentApi.updateAgent(this._editAgent.id, data);
      this._editSuccess = 'Gespeichert';
      await this._loadTeam();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Speichern';
    } finally {
      this._editSaving = false;
    }
  }

  private get _personsByCategory(): [string, PersonSummary[]][] {
    const groups = new Map<string, PersonSummary[]>();
    for (const p of this._availablePersons) {
      const cat = p.category || 'Sonstige';
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat)!.push(p);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }

  private get _filteredTemplates(): AgentTemplate[] {
    if (!this._templateSearch.trim()) return this._templates;
    const q = this._templateSearch.toLowerCase();
    return this._templates.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q) ||
        t.tags.some((tag) => tag.toLowerCase().includes(q)),
    );
  }

  private _getOrgTree(): { ceo: CompanyAgent | null; leads: Map<number, CompanyAgent[]>; leadAgents: CompanyAgent[] } {
    const ceo = this._agents.find(a => a.role === 'ceo') ?? null;
    const leadAgents = this._agents.filter(a => a.role === 'department_lead');
    const employees = this._agents.filter(a => a.role === 'employee' || !a.role);

    const leads = new Map<number, CompanyAgent[]>();
    for (const lead of leadAgents) {
      leads.set(lead.id, []);
    }

    for (const emp of employees) {
      if (emp.parent_agent_id && leads.has(emp.parent_agent_id)) {
        leads.get(emp.parent_agent_id)!.push(emp);
      } else if (ceo) {
        // Unassigned employees go under CEO
        if (!leads.has(ceo.id)) leads.set(ceo.id, []);
        leads.get(ceo.id)!.push(emp);
      }
    }

    return { ceo, leads, leadAgents };
  }

  private _renderTeamView() {
    if (this._loading) {
      return html`<p class="cp-team-overview__loading">Team wird geladen...</p>`;
    }

    if (this._agents.length === 0) {
      return html`
        <div class="cp-team-overview__empty">
          <p class="cp-team-overview__empty-text">Noch keine Team-Mitglieder</p>
          <button class="cp-team-overview__empty-btn" @click=${() => this._switchView('hire')}>
            Ersten Mitarbeiter einstellen
          </button>
        </div>
      `;
    }

    const { ceo, leads, leadAgents } = this._getOrgTree();
    const ceoDirectReports = ceo ? (leads.get(ceo.id) ?? []) : [];
    const hasChildren = leadAgents.length > 0 || ceoDirectReports.length > 0;

    return html`
      <ul class="cp-tree">
        ${ceo ? html`
          <li class="cp-tree__item">
            ${this._renderTreeRow(ceo)}
            ${hasChildren ? html`
              <ul class="cp-tree__children">
                ${leadAgents.map(lead => {
                  const members = leads.get(lead.id) ?? [];
                  return html`
                    <li class="cp-tree__item">
                      ${this._renderTreeRow(lead)}
                      ${members.length > 0 ? html`
                        <ul class="cp-tree__children">
                          ${members.map(m => html`
                            <li class="cp-tree__item">${this._renderTreeRow(m)}</li>
                          `)}
                        </ul>
                      ` : nothing}
                    </li>
                  `;
                })}
                ${ceoDirectReports.map(m => html`
                  <li class="cp-tree__item">${this._renderTreeRow(m)}</li>
                `)}
              </ul>
            ` : nothing}
          </li>
        ` : html`
          ${this._agents.map(a => html`
            <li class="cp-tree__item">${this._renderTreeRow(a)}</li>
          `)}
        `}
      </ul>
    `;
  }

  private _renderTreeRow(agent: CompanyAgent) {
    const initials = agent.name.split(' ').filter((w: string) => w.length > 0).map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();
    const isDeactivated = agent.status === 'deactivated';
    const isCeo = agent.role === 'ceo';

    const roleLabel: Record<string, string> = { ceo: 'CEO', department_lead: 'Lead', employee: 'Mitarbeiter' };
    const statusLabel: Record<string, string> = { active: 'Aktiv', paused: 'Pausiert', deactivated: 'Deaktiviert' };

    return html`
      <div
        class="cp-tree__row ${isDeactivated ? 'cp-tree__row--deactivated' : ''} cp-tree__row--${agent.role ?? 'employee'}"
        @click=${() => this._openAgentEdit(agent.id)}
      >
        <div class="cp-tree__avatar" style="background-color: ${agent.color}">${initials}</div>
        <div class="cp-tree__main">
          <span class="cp-tree__name">${agent.name}</span>
          <span class="cp-tree__type">${agent.personality_type || '–'}</span>
        </div>
        <div class="cp-tree__badges">
          <span class="cp-team-overview__badge cp-team-overview__badge--${agent.role ?? 'employee'}">${agent.role ? (roleLabel[agent.role] ?? agent.role) : '–'}</span>
          <span class="cp-team-overview__badge cp-team-overview__badge--${agent.status}">${statusLabel[agent.status] ?? agent.status}</span>
        </div>
        ${!isCeo ? html`
          <div class="cp-tree__actions">
            ${!isDeactivated ? html`
              <button class="cp-team-overview__deactivate-btn" @click=${(e: Event) => { e.stopPropagation(); this._deactivateAgent(agent.id); }}>
                Deaktivieren
              </button>
            ` : nothing}
            <button class="cp-team-overview__delete-btn" @click=${(e: Event) => { e.stopPropagation(); this._deleteAgent(agent.id); }}>
              Loeschen
            </button>
          </div>
        ` : nothing}
      </div>
    `;
  }

  private _renderAgentEdit() {
    const a = this._editAgent!;

    return html`
      <div style="display:flex;flex-direction:column;gap:var(--spacing-md);">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <button class="cp-team-overview__hire-back" @click=${() => this._closeAgentEdit()}>
            ← Zurueck zum Team
          </button>
          ${a.role !== 'ceo' ? html`
            <button class="cp-team-overview__delete-btn" @click=${() => this._deleteAgent(a.id)}>
              Agent loeschen
            </button>
          ` : nothing}
        </div>

        ${this._editSuccess ? html`
          <div class="cp-team-overview__success">${this._editSuccess}</div>
        ` : nothing}

        <div class="cp-team-overview__edit-section">
          <p class="cp-team-overview__section-label">Einstellungen</p>
          <div class="cp-team-overview__edit-grid">
            <label class="cp-team-overview__field">
              <span class="cp-team-overview__label">Name</span>
              <input
                class="cp-team-overview__input"
                type="text"
                .value=${this._editName}
                @input=${(e: Event) => { this._editName = (e.target as HTMLInputElement).value; this._editSuccess = ''; }}
              />
            </label>
            <label class="cp-team-overview__field">
              <span class="cp-team-overview__label">Farbe</span>
              <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                <input
                  type="color"
                  .value=${this._editColor}
                  @input=${(e: Event) => { this._editColor = (e.target as HTMLInputElement).value; this._editSuccess = ''; }}
                  style="width:36px;height:36px;border:none;background:none;cursor:pointer;padding:0;"
                />
                <span style="font-size:var(--font-size-xs);color:var(--color-text-secondary);">${this._editColor}</span>
              </div>
            </label>
            <label class="cp-team-overview__field">
              <span class="cp-team-overview__label">Provider</span>
              <select
                class="cp-team-overview__input"
                .value=${this._editProvider}
                @change=${(e: Event) => void this._onEditProviderChange(e)}
              >
                ${this._editProviders.map(p => html`
                  <option value=${p.id} ?selected=${this._editProvider === p.id}>${p.displayName}</option>
                `)}
              </select>
            </label>
            <label class="cp-team-overview__field">
              <span class="cp-team-overview__label">Modell</span>
              <select
                class="cp-team-overview__input"
                .value=${this._editModel}
                @change=${(e: Event) => this._onEditModelChange(e)}
              >
                ${this._editModelsForProvider.map(m => html`
                  <option value=${m} ?selected=${this._editModel === m}>${m}</option>
                `)}
              </select>
            </label>
            <label class="cp-team-overview__field">
              <span class="cp-team-overview__label">Max Tokens</span>
              <input
                class="cp-team-overview__input"
                type="number"
                min="256"
                max="32768"
                step="256"
                .value=${String(this._editMaxTokens)}
                @input=${(e: Event) => { this._editMaxTokens = Number((e.target as HTMLInputElement).value); this._editSuccess = ''; }}
              />
            </label>
          </div>
          <button
            class="cp-team-overview__submit-btn"
            @click=${() => void this._saveAgentSettings()}
            ?disabled=${this._editSaving}
            style="margin-top:var(--spacing-sm);"
          >
            ${this._editSaving ? 'Speichert...' : 'Einstellungen speichern'}
          </button>
        </div>

        <div class="cp-team-overview__edit-section">
          <p class="cp-team-overview__section-label">MCP-Tools</p>
          <p style="font-size:var(--font-size-xs);color:var(--color-text-secondary);margin:0;">
            Leer = alle Tools erlaubt. Ausgewaehlte Tools beschraenken den Agent auf diese Tools.
          </p>

          ${this._toolsSuccess ? html`
            <div class="cp-team-overview__success">${this._toolsSuccess}</div>
          ` : nothing}

          <div class="cp-team-overview__tools-section">
            ${this._availableTools.filter(g => g.server !== 'builtin').map(group => {
              const allSelected = group.tools.every(t => this._selectedTools.has(t.fullName));
              const someSelected = group.tools.some(t => this._selectedTools.has(t.fullName));
              const isExpanded = this._expandedGroups.has(group.server);
              const selectedCount = group.tools.filter(t => this._selectedTools.has(t.fullName)).length;

              return html`
                <div class="cp-team-overview__tools-group">
                  <button
                    class="cp-team-overview__tools-group-header"
                    @click=${() => this._toggleToolGroup(group.server)}
                  >
                    <span style="display:flex;align-items:center;gap:var(--spacing-sm);flex:1;min-width:0;">
                      <span style="font-size:var(--font-size-xs);color:var(--color-text-tertiary);">${isExpanded ? '▼' : '▶'}</span>
                      <span>${group.server}</span>
                      <span style="font-size:var(--font-size-xs);color:var(--color-text-tertiary);">(${selectedCount}/${group.tools.length})</span>
                    </span>
                    <label
                      style="display:flex;align-items:center;gap:var(--spacing-xs);cursor:pointer;"
                      @click=${(e: Event) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        .checked=${allSelected}
                        .indeterminate=${someSelected && !allSelected}
                        @change=${() => this._toggleAllInGroup(group)}
                      />
                      <span style="font-size:var(--font-size-xs);color:var(--color-text-secondary);">Alle</span>
                    </label>
                  </button>
                  ${isExpanded ? html`
                    <div class="cp-team-overview__tools-list">
                      ${group.tools.map(tool => html`
                        <label class="cp-team-overview__tool-item">
                          <input
                            type="checkbox"
                            .checked=${this._selectedTools.has(tool.fullName)}
                            @change=${() => this._toggleTool(tool.fullName)}
                          />
                          <span>${tool.name}</span>
                        </label>
                      `)}
                    </div>
                  ` : nothing}
                </div>
              `;
            })}
          </div>

          <button
            class="cp-team-overview__submit-btn"
            @click=${() => void this._saveAllowedTools()}
            ?disabled=${this._toolsSaving}
          >
            ${this._toolsSaving ? 'Speichert...' : 'Tools speichern'}
          </button>
        </div>

        <div class="cp-team-overview__edit-section">
          <p class="cp-team-overview__section-label">Personen-Zugriff</p>
          <p style="font-size:var(--font-size-xs);color:var(--color-text-secondary);margin:0;">
            Leer = alle Personen. Ausgewaehlte Personen beschraenken den Zugriff.
          </p>

          ${this._personsSuccess ? html`
            <div class="cp-team-overview__success">${this._personsSuccess}</div>
          ` : nothing}

          ${this._availablePersons.length === 0 ? html`
            <p style="font-size:var(--font-size-xs);color:var(--color-text-tertiary);margin:var(--spacing-sm) 0;">
              Keine Personen vorhanden
            </p>
          ` : html`
            <div class="cp-team-overview__tools-section">
              ${this._personsByCategory.map(([category, persons]) => html`
                <div class="cp-team-overview__tools-group">
                  <div class="cp-team-overview__tools-group-header" style="cursor:default;">
                    <span style="font-size:var(--font-size-xs);font-weight:600;">${category}</span>
                    <span style="font-size:var(--font-size-xs);color:var(--color-text-tertiary);">
                      (${persons.filter(p => this._selectedPersons.has(p.id)).length}/${persons.length})
                    </span>
                  </div>
                  <div class="cp-team-overview__tools-list">
                    ${persons.map(person => html`
                      <label class="cp-team-overview__tool-item">
                        <input
                          type="checkbox"
                          .checked=${this._selectedPersons.has(person.id)}
                          @change=${() => this._togglePerson(person.id)}
                        />
                        <span>${person.full_name}</span>
                      </label>
                    `)}
                  </div>
                </div>
              `)}
            </div>
          `}

          <button
            class="cp-team-overview__submit-btn"
            @click=${() => void this._saveAllowedPersons()}
            ?disabled=${this._personsSaving}
          >
            ${this._personsSaving ? 'Speichert...' : 'Personen speichern'}
          </button>
        </div>

        ${this._availableSkills.length > 0 ? html`
          <div class="cp-team-overview__edit-section">
            <p class="cp-team-overview__section-label">Skills</p>
            <p style="font-size:var(--font-size-xs);color:var(--color-text-secondary);margin:0;">
              Ausgewaehlte Skills stehen dem Agent zur Verfuegung.
            </p>

            ${this._skillsSuccess ? html`
              <div class="cp-team-overview__success">${this._skillsSuccess}</div>
            ` : nothing}

            <div class="cp-team-overview__skills-grid">
              ${this._availableSkills.map(skill => {
                const selected = this._selectedSkills.has(skill.id);
                return html`
                  <label class="cp-team-overview__skill-card ${selected ? 'cp-team-overview__skill-card--selected' : ''}">
                    <input
                      type="checkbox"
                      .checked=${selected}
                      @change=${() => this._toggleSkill(skill.id)}
                    />
                    <div class="cp-team-overview__skill-info">
                      <span class="cp-team-overview__skill-name">${skill.name}</span>
                      ${skill.description ? html`
                        <span class="cp-team-overview__skill-desc">${skill.description}</span>
                      ` : nothing}
                    </div>
                  </label>
                `;
              })}
            </div>

            <button
              class="cp-team-overview__submit-btn"
              @click=${() => void this._saveAllowedSkills()}
              ?disabled=${this._skillsSaving}
            >
              ${this._skillsSaving ? 'Speichert...' : 'Skills speichern'}
            </button>
          </div>
        ` : nothing}

        <div class="cp-team-overview__edit-section">
          <p class="cp-team-overview__section-label">Persoenlichkeit & Prompt</p>
          <cp-soul-editor .agentId=${a.id}></cp-soul-editor>
        </div>
      </div>
    `;
  }

  private _renderHireView() {
    if (this._selectedTemplate) {
      return this._renderHireForm();
    }
    return this._renderTemplateBrowser();
  }

  private _renderTemplateBrowser() {
    return html`
      <div style="display:flex;flex-direction:column;gap:var(--spacing-md);">
        <input
          type="text"
          placeholder="Template suchen..."
          .value=${this._templateSearch}
          @input=${(e: Event) => { this._templateSearch = (e.target as HTMLInputElement).value; }}
          class="cp-team-overview__search-input"
        />
        ${this._filteredTemplates.length === 0
          ? html`<p class="cp-team-overview__loading">Keine Templates gefunden</p>`
          : html`
            <div class="cp-team-overview__template-grid">
              ${this._filteredTemplates.map((t) => html`
                <button class="cp-team-overview__template-card" @click=${() => this._selectTemplate(t)}>
                  <div class="cp-team-overview__template-header">
                    <p class="cp-team-overview__template-name">${t.name}</p>
                    <span class="cp-team-overview__template-category">${t.category}</span>
                  </div>
                  <p class="cp-team-overview__template-desc">${t.description}</p>
                  <div class="cp-team-overview__template-tags">
                    ${t.tags.slice(0, 3).map((tag: string) => html`
                      <span class="cp-team-overview__template-tag">${tag}</span>
                    `)}
                  </div>
                </button>
              `)}
            </div>
          `}
      </div>
    `;
  }

  private _renderHireForm() {
    const t = this._selectedTemplate!;
    return html`
      <div style="display:flex;flex-direction:column;gap:var(--spacing-md);">
        <button class="cp-team-overview__hire-back" @click=${() => { this._selectedTemplate = null; }}>
          ← Zurueck zur Template-Auswahl
        </button>

        <div class="cp-team-overview__hire-info">
          <p class="cp-team-overview__hire-info-name">${t.name}</p>
          <p class="cp-team-overview__hire-info-desc">${t.description}</p>
        </div>

        <div class="cp-team-overview__field">
          <label class="cp-team-overview__label">Spezialisierung (optional)</label>
          <input
            type="text"
            placeholder="z.B. Instagram & TikTok Fokus"
            .value=${this._specialization}
            @input=${(e: Event) => { this._specialization = (e.target as HTMLInputElement).value; }}
            class="cp-team-overview__input"
          />
        </div>

        <div class="cp-team-overview__field">
          <label class="cp-team-overview__label">Begruendung *</label>
          <textarea
            placeholder="Warum wird dieser Mitarbeiter benoetigt?"
            .value=${this._reason}
            @input=${(e: Event) => { this._reason = (e.target as HTMLTextAreaElement).value; }}
            rows="3"
            class="cp-team-overview__textarea"
          ></textarea>
        </div>

        <button
          @click=${this._submitHireRequest}
          ?disabled=${this._submitting || !this._reason.trim()}
          class="cp-team-overview__submit-btn"
        >
          ${this._submitting ? 'Wird eingereicht...' : 'Einstellungs-Antrag stellen'}
        </button>
      </div>
    `;
  }

  private _renderPendingView() {
    const pending = this._pendingRequests.filter((r) => r.status === 'pending');
    const processed = this._pendingRequests.filter((r) => r.status !== 'pending');

    return html`
      <div style="display:flex;flex-direction:column;gap:var(--spacing-md);">
        ${pending.length === 0
          ? html`<p class="cp-team-overview__loading">Keine offenen Antraege</p>`
          : html`
            <div style="display:flex;flex-direction:column;gap:var(--spacing-sm);">
              <p class="cp-team-overview__section-label">Offene Antraege (${pending.length})</p>
              <div class="cp-team-overview__request-list">
                ${pending.map((r) => html`
                  <div class="cp-team-overview__request-card">
                    <div>
                      <p class="cp-team-overview__request-name">${r.template_name}${r.specialization ? ` — ${r.specialization}` : ''}</p>
                      <p class="cp-team-overview__request-reason">${r.reason}</p>
                    </div>
                    <div class="cp-team-overview__request-actions">
                      <button class="cp-team-overview__btn-approve" @click=${() => this._approveRequest(r.id)}>
                        Genehmigen
                      </button>
                      <button class="cp-team-overview__btn-reject" @click=${() => this._rejectRequest(r.id)}>
                        Ablehnen
                      </button>
                    </div>
                  </div>
                `)}
              </div>
            </div>
          `}
        ${processed.length > 0 ? html`
          <div style="display:flex;flex-direction:column;gap:var(--spacing-sm);">
            <p class="cp-team-overview__section-label">Bearbeitet</p>
            ${processed.map((r) => html`
              <div class="cp-team-overview__processed-card">
                <div>
                  <p class="cp-team-overview__processed-name">${r.template_name}${r.specialization ? ` — ${r.specialization}` : ''}</p>
                  ${r.rejection_comment ? html`<p class="cp-team-overview__processed-comment">${r.rejection_comment}</p>` : nothing}
                </div>
                <span class="cp-team-overview__processed-badge ${r.status === 'approved' ? 'cp-team-overview__processed-badge--approved' : 'cp-team-overview__processed-badge--rejected'}">
                  ${r.status === 'approved' ? 'Genehmigt' : 'Abgelehnt'}
                </span>
              </div>
            `)}
          </div>
        ` : nothing}
      </div>
    `;
  }

  override render() {
    const pendingCount = this._pendingRequests.filter((r) => r.status === 'pending').length;

    return html`
      <div style="display:flex;flex-direction:column;gap:var(--spacing-md);">
        ${this._error ? html`
          <div class="cp-team-overview__error">${this._error}</div>
        ` : nothing}

        <div class="cp-team-overview__nav">
          <button
            @click=${() => this._switchView('team')}
            class="cp-team-overview__nav-btn ${this._view === 'team' ? 'cp-team-overview__nav-btn--active' : ''}"
          >
            Team (${this._agents.length})
          </button>
          <button
            @click=${() => this._switchView('hire')}
            class="cp-team-overview__nav-btn ${this._view === 'hire' ? 'cp-team-overview__nav-btn--active' : ''}"
          >
            Einstellen
          </button>
          <button
            @click=${() => this._switchView('pending')}
            class="cp-team-overview__nav-btn ${this._view === 'pending' ? 'cp-team-overview__nav-btn--active' : ''}"
          >
            Antraege${pendingCount > 0 ? ` (${pendingCount})` : ''}
          </button>
        </div>

        ${this._editAgent ? this._renderAgentEdit() : html`
          ${this._view === 'team' ? this._renderTeamView() : nothing}
          ${this._view === 'hire' ? this._renderHireView() : nothing}
          ${this._view === 'pending' ? this._renderPendingView() : nothing}
        `}
      </div>
    `;
  }
}
